import React, { useState, useEffect, useRef } from "react";
import { Cpu } from "lucide-react";
import { useTranslation } from "react-i18next";
import betaAPI from "v2/api";
import { showToast } from "utils/toast";
import ModalShell from "./ModalShell";
import { TextField, SelectField } from "./FormField";
import {
  formatApiError,
  getUnitTypeOptions,
  buildSerial,
  incrementTrailingNumber,
  parseFieldLabel,
  FIELD_LABEL_MAX,
} from "./constants";

const emptyForm = {
  ble_mac: "",
  serial: "",
  batch_id: "",
  sequence_number: "",
  unit_type: "production",
  field_label: "",
};

/**
 * Create / allocate a wearable, or edit an existing one's mutable fields.
 * Pass `wearable` to edit; otherwise it's a create form.
 * `defaultBatchId` preselects a batch (e.g. when allocating from a batch).
 *
 * In create mode the serial auto-derives from the chosen batch + sequence
 * (SIT-{hw}-{code}-{seq}), and an "add another" option keeps the modal open
 * after allocating — incrementing the sequence and serial while clearing only
 * the BLE MAC and calibration so the next unit can be entered quickly.
 */
const WearableFormModal = ({
  show,
  onClose,
  wearable,
  defaultBatchId,
  onSaved,
}) => {
  const { t } = useTranslation();
  const isEdit = Boolean(wearable);
  const [form, setForm] = useState(emptyForm);
  const [batches, setBatches] = useState([]);
  const [serialEdited, setSerialEdited] = useState(false);
  const [addAnother, setAddAnother] = useState(false);
  // Registration IS the Baseline Test factory_test — capture its result here.
  const [factoryResult, setFactoryResult] = useState("pass");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const bleMacRef = useRef(null);

  const selectedBatch = batches.find((b) => b.id === form.batch_id);

  // Reset the form whenever the modal opens
  useEffect(() => {
    if (!show) return;
    setError(null);
    setSerialEdited(false);
    setAddAnother(false);
    setFactoryResult("pass");
    if (wearable) {
      setForm({
        ...emptyForm,
        serial: wearable.serial ?? "",
        unit_type: wearable.unit_type ?? "production",
        field_label:
          wearable.field_label != null ? String(wearable.field_label) : "",
      });
    } else {
      setForm({ ...emptyForm, batch_id: defaultBatchId || "" });
    }
  }, [show, wearable, defaultBatchId]);

  // Load batches for the selector (create mode only) and seed the default
  // batch / sequence / serial so the form is ready to fill.
  useEffect(() => {
    if (!show || isEdit) return;
    let active = true;
    (async () => {
      try {
        const res = await betaAPI.getBatches({ page: 1, pageSize: 100 });
        if (!active) return;
        const list = res.data?.results || [];
        setBatches(list);
        setForm((prev) => {
          const chosenId = prev.batch_id || defaultBatchId || list[0]?.id || "";
          const batch = list.find((b) => b.id === chosenId);
          if (!batch) return { ...prev, batch_id: chosenId };
          const seq =
            prev.sequence_number || String((batch.wearable_count ?? 0) + 1);
          return {
            ...prev,
            batch_id: chosenId,
            sequence_number: seq,
            serial: buildSerial(batch, seq),
          };
        });
      } catch {
        // selector will just be empty; surfaced on submit if needed
      }
    })();
    return () => {
      active = false;
    };
  }, [show, isEdit, defaultBatchId]);

  const set = (key) => (value) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  // Selecting a batch re-seeds the sequence (next available) and serial.
  const handleBatchChange = (batchId) => {
    const batch = batches.find((b) => b.id === batchId);
    setForm((prev) => {
      const seq = String((batch?.wearable_count ?? 0) + 1);
      return {
        ...prev,
        batch_id: batchId,
        sequence_number: seq,
        serial: serialEdited ? prev.serial : buildSerial(batch, seq),
      };
    });
  };

  // Changing the sequence keeps the serial's trailing number in sync.
  const handleSequenceChange = (value) => {
    setForm((prev) => ({
      ...prev,
      sequence_number: value,
      serial: serialEdited ? prev.serial : buildSerial(selectedBatch, value),
    }));
  };

  const handleSerialChange = (value) => {
    setSerialEdited(true);
    setForm((prev) => ({ ...prev, serial: value }));
  };

  const seqNum = parseInt(form.sequence_number, 10);
  const labelParsed = parseFieldLabel(form.field_label);
  const isValid =
    labelParsed.valid &&
    (isEdit
      ? form.serial.trim().length > 0
      : form.ble_mac.replace(/[^0-9a-fA-F]/g, "").length === 12 &&
        form.serial.trim().length > 0 &&
        form.batch_id &&
        Number.isInteger(seqNum) &&
        seqNum >= 0);

  // After a successful register-and-add-another: keep the batch and unit type,
  // bump the sequence + serial, and clear the per-unit BLE MAC ready for the
  // next unit at Baseline Test.
  const prepareNext = () => {
    setForm((prev) => ({
      ...prev,
      ble_mac: "",
      sequence_number: String((parseInt(prev.sequence_number, 10) || 0) + 1),
      serial: incrementTrailingNumber(prev.serial),
      // The printed label is per-unit; clear it so the operator sets each.
      field_label: "",
    }));
    setFactoryResult("pass");
    setError(null);
    requestAnimationFrame(() => bleMacRef.current?.focus());
  };

  const handleSubmit = async () => {
    if (!isValid) return;

    setSaving(true);
    setError(null);

    try {
      if (isEdit) {
        // Serial, unit_type and the printed label are editable here; status
        // moves live in the lifecycle control. field_label sends null to clear.
        const res = await betaAPI.updateWearable({
          bleMac: wearable.ble_mac,
          body: {
            serial: form.serial.trim(),
            unit_type: form.unit_type,
            field_label: labelParsed.value,
          },
        });
        showToast.success(t("admin.manufacturing.wearableForm.updated"));
        onSaved?.(res.data);
        onClose();
      } else {
        // Devices are created at Baseline Test with the MAC as identifier.
        const created = await betaAPI.createWearable({
          body: {
            ble_mac: form.ble_mac,
            serial: form.serial.trim(),
            batch_id: form.batch_id,
            unit_type: form.unit_type,
            // Optional printed label; omit when unset rather than sending null.
            ...(labelParsed.value != null
              ? { field_label: labelParsed.value }
              : {}),
          },
        });
        // Registration IS the Baseline Test factory_test (it captures the MAC),
        // so log that step's result in the same action — the operator shouldn't
        // have to re-enter the MAC on the detail page. A pass advances the device
        // to the next sub-stage (overmould); a fail leaves it at baseline for
        // re-test.
        const mac = created.data?.ble_mac || form.ble_mac;
        let detail = created.data;
        try {
          const res = await betaAPI.createWearableAttempt({
            bleMac: mac,
            body: {
              sub_stage: "baseline",
              step: "factory_test",
              result: factoryResult,
              data: { mac },
            },
          });
          detail = res.data;
        } catch {
          // If the backend already auto-completes baseline on register, this
          // out-of-order attempt is rejected — harmless, the create response is
          // already correct and the device is registered either way.
        }
        showToast.success(
          factoryResult === "pass"
            ? t("admin.manufacturing.wearableForm.registered")
            : t("admin.manufacturing.wearableForm.registeredFactoryFailed")
        );
        onSaved?.(detail);
        if (addAnother) {
          prepareNext();
        } else {
          onClose();
        }
      }
    } catch (err) {
      setError(
        formatApiError(err, t("admin.manufacturing.wearableForm.saveFailed"))
      );
    } finally {
      setSaving(false);
    }
  };

  const batchOptions = batches.map((b) => ({ id: b.id, label: b.code }));

  return (
    <ModalShell
      show={show}
      onClose={onClose}
      icon={Cpu}
      title={
        isEdit
          ? t("admin.manufacturing.wearableForm.editTitle")
          : t("admin.manufacturing.wearableForm.registerTitle")
      }
      subtitle={
        isEdit
          ? t("admin.manufacturing.wearableForm.editSubtitle", {
              serial: wearable?.serial,
            })
          : t("admin.manufacturing.wearableForm.registerSubtitle")
      }
      submitLabel={
        isEdit
          ? t("admin.manufacturing.wearableForm.saveChanges")
          : addAnother
          ? t("admin.manufacturing.wearableForm.registerAndAddAnother")
          : t("admin.manufacturing.wearableForm.register")
      }
      submitting={saving}
      submitDisabled={!isValid}
      onSubmit={handleSubmit}
      error={error}
    >
      <div className="grid grid-cols-2 gap-4">
        {!isEdit && (
          <>
            <TextField
              className="col-span-2"
              label={t("admin.manufacturing.wearableForm.bleMac")}
              value={form.ble_mac}
              onChange={set("ble_mac")}
              placeholder="A1:B2:C3:D4:E5:F6"
              required
              hint={t("admin.manufacturing.wearableForm.bleMacHint")}
              inputRef={bleMacRef}
            />
            <SelectField
              label={t("admin.manufacturing.wearableForm.batch")}
              value={form.batch_id}
              onChange={handleBatchChange}
              options={batchOptions}
              placeholder={t(
                "admin.manufacturing.wearableForm.batchPlaceholder"
              )}
              required
            />
            <TextField
              label={t("admin.manufacturing.wearableForm.sequence")}
              value={form.sequence_number}
              onChange={handleSequenceChange}
              placeholder="1"
              type="number"
              required
            />
          </>
        )}

        <TextField
          label={t("admin.manufacturing.wearableForm.serial")}
          value={form.serial}
          onChange={isEdit ? set("serial") : handleSerialChange}
          placeholder="SIT-W1-2624-00001"
          required
          maxLength={24}
          hint={
            !isEdit && !serialEdited
              ? t("admin.manufacturing.wearableForm.serialHint")
              : undefined
          }
        />
        <TextField
          label={t("admin.manufacturing.wearableForm.printedLabel")}
          value={form.field_label}
          onChange={set("field_label")}
          placeholder={t(
            "admin.manufacturing.wearableForm.printedLabelPlaceholder"
          )}
          type="number"
          hint={t("admin.manufacturing.wearableForm.printedLabelHint", {
            max: FIELD_LABEL_MAX,
          })}
        />
        <SelectField
          className="col-span-2"
          label={t("admin.manufacturing.wearableForm.unitType")}
          value={form.unit_type}
          onChange={set("unit_type")}
          options={getUnitTypeOptions()}
          placeholder=""
        />

        {/* Registration IS the Baseline Test — record the factory test result
            here rather than as a separate step afterwards. */}
        {!isEdit && (
          <div className="col-span-2">
            <label className="mb-2 block text-sm font-medium text-[#374151]">
              {t("admin.manufacturing.wearableForm.factoryResult")}
            </label>
            <div className="flex gap-2">
              {[
                {
                  id: "pass",
                  label: t("admin.manufacturing.wearableForm.pass"),
                  on: "border-[#16A34A] bg-[#F0FDF4] text-[#16A34A]",
                },
                {
                  id: "fail",
                  label: t("admin.manufacturing.wearableForm.fail"),
                  on: "border-[#DC2626] bg-[#FEF2F2] text-[#DC2626]",
                },
              ].map((opt) => {
                const active = factoryResult === opt.id;
                return (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => setFactoryResult(opt.id)}
                    className={`flex-1 rounded-[10px] border px-4 py-2.5 text-sm font-semibold transition duration-150 ${
                      active
                        ? opt.on
                        : "border-[#E5E7EB] bg-white text-[#6B7280] hover:bg-[#F9FAFB]"
                    }`}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
            <p className="mt-1.5 text-xs text-[#9CA3AF]">
              {factoryResult === "pass"
                ? t("admin.manufacturing.wearableForm.factoryPassHint")
                : t("admin.manufacturing.wearableForm.factoryFailHint")}
            </p>
          </div>
        )}

        {!isEdit && (
          <label className="col-span-2 flex cursor-pointer items-center gap-2.5 rounded-lg border border-[#F3F4F6] bg-[#F9FAFB] px-3.5 py-3">
            <input
              type="checkbox"
              checked={addAnother}
              onChange={(e) => setAddAnother(e.target.checked)}
              className="h-4 w-4 cursor-pointer accent-[#E07A3A]"
            />
            <span className="text-sm text-[#374151]">
              {t("admin.manufacturing.wearableForm.addAnother")}
            </span>
            <span className="text-xs text-[#9CA3AF]">
              {t("admin.manufacturing.wearableForm.addAnotherHint")}
            </span>
          </label>
        )}
      </div>
    </ModalShell>
  );
};

export default WearableFormModal;
